/* Shared storefront single-choice menus. The original select remains the form and app authority. */
(() => {
  'use strict';
  const candidates = [
    'select#contact_topic', 'select[data-productgrid-trigger-sort]',
    'select.collection-sort__input[name="sort_by"]', 'select[data-live-search-filter]',
    'select[data-quantity-select]', '.cbb-frequently-bought-selector-list select',
    'select[data-variant-option]', '.account-address-form select[name="address[country]"]',
    '.account-address-form select[name="address[province]"]',
    '#shipping-calculator select#address_country', '#shipping-calculator select#address_province'
  ].join(',');
  const excluded = '[data-variants], .variants-ui__select-main, .hux-size-select__native, [data-hux-size-select], [data-hux-firmness], [data-hux-dropdown-skip]';
  const instances = new Map();
  let serial = 0;
  let opened = null;
  let pending = false;
  let validationFocus = false;
  let liveRegion;

  const eligible = select => !select.multiple && select.size <= 1 && !select.matches(excluded) && !select.closest('noscript');
  const setAttr = (node, name, value) => {
    if (value == null) { if (node.hasAttribute(name)) node.removeAttribute(name); }
    else if (node.getAttribute(name) !== String(value)) node.setAttribute(name, value);
  };
  const setText = (node, value) => { if (node.textContent !== value) node.textContent = value; };
  function available(element) {
    if (!element.isConnected || element.hidden || element.closest('[hidden], [inert]')) return false;
    // Empire's 10+ branch uses a class whose visibility rule can lose to its base rule.
    if (element.closest('.form-field--qty-select.hidden')) return false;
    for (let parent = element.parentElement; parent; parent = parent.parentElement) {
      if (parent.getAttribute('aria-hidden') === 'true') return false;
    }
    const style = getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }
  function kindOf(select) {
    if (select.matches('[data-live-search-filter]')) return 'search';
    if (select.matches('[data-quantity-select]')) return 'quantity';
    if (select.closest('.cbb-frequently-bought-selector-list')) return 'bundle';
    if (select.matches('[data-productgrid-trigger-sort], .collection-sort__input')) return 'sort';
    if (select.matches('[data-variant-option]')) return 'variant';
    return select.id === 'contact_topic' ? 'contact' : 'address';
  }
  function labelFor(select, kind) {
    if (kind === 'bundle') {
      const title = select.closest('li')?.querySelector('.cbb-frequently-bought-selector-label-name, .cbb-frequently-bought-selector-link');
      if (title) return { text: title.textContent.trim() + ' options' };
    }
    if (select.getAttribute('aria-labelledby')) return { ids: select.getAttribute('aria-labelledby') };
    const labels = Array.from(select.labels || []);
    if (labels.length) return { ids: labels.map(label => {
      if (!label.id) label.id = 'hux-dropdown-label-' + (++serial);
      return label.id;
    }).join(' ') };
    return { text: select.getAttribute('aria-label') || (kind === 'search' ? 'Search category' : kind === 'quantity' ? 'Quantity' : 'Choose an option') };
  }
  function announce(message) {
    if (!liveRegion) {
      liveRegion = document.createElement('span');
      liveRegion.id = 'hux-dropdown-validation';
      liveRegion.dataset.huxDropdownError = '';
      liveRegion.setAttribute('aria-live', 'polite');
      document.body.appendChild(liveRegion);
    }
    setText(liveRegion, message);
    return liveRegion.id;
  }

  class Dropdown {
    constructor(select) {
      this.select = select;
      this.kind = kindOf(select);
      this.id = 'hux-dropdown-' + (++serial);
      this.oldTab = select.getAttribute('tabindex');
      this.oldHidden = select.getAttribute('aria-hidden');
      this.active = -1;
      this.rows = [];
      this.buffer = '';
      this.typedAt = 0;
      this.trigger = document.createElement('button');
      this.trigger.id = this.id;
      this.trigger.type = 'button';
      this.trigger.dataset.huxDropdownTrigger = '';
      this.trigger.dataset.huxDropdownKind = this.kind;
      this.trigger.setAttribute('role', 'combobox');
      this.trigger.setAttribute('aria-haspopup', 'listbox');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.trigger.setAttribute('aria-controls', this.id + '-menu');
      this.value = document.createElement('span');
      this.value.className = 'hux-dropdown__value';
      const chevron = document.createElement('span');
      chevron.className = 'hux-dropdown__chevron';
      chevron.setAttribute('aria-hidden', 'true');
      this.trigger.append(this.value, chevron);
      this.error = document.createElement('span');
      this.error.id = this.id + '-error';
      this.error.dataset.huxDropdownError = '';
      this.menu = document.createElement('ul');
      this.menu.id = this.id + '-menu';
      this.menu.dataset.huxDropdownMenu = '';
      this.menu.className = 'allow-scroll-while-locked';
      this.menu.setAttribute('role', 'listbox');
      this.menu.hidden = true;
      const brand = select.closest('[data-hux-dropdown-brand]')?.getAttribute('data-hux-dropdown-brand');
      if (brand) {
        this.trigger.dataset.huxDropdownBrand = brand;
        this.menu.dataset.huxDropdownBrand = brand;
      }
      select.insertAdjacentElement('afterend', this.trigger);
      this.trigger.insertAdjacentElement('afterend', this.error);
      (select.closest('[role="dialog"], dialog, [data-modal-container], [data-modal-container-1]') || document.body).appendChild(this.menu);
      this.onClick = () => opened === this ? this.close() : this.open();
      this.onKey = event => this.key(event);
      this.onFocus = () => this.sync();
      this.onNative = () => { this.invalid = false; this.sync(); };
      this.onNativeFocus = () => { if (available(this.trigger)) this.trigger.focus({ preventScroll: true }); };
      this.trigger.addEventListener('click', this.onClick);
      this.trigger.addEventListener('keydown', this.onKey);
      this.trigger.addEventListener('focus', this.onFocus);
      select.addEventListener('change', this.onNative);
      select.addEventListener('input', this.onNative);
      select.addEventListener('focus', this.onNativeFocus);
      // Portals must not be treated as outside clicks by the theme's search/modal controllers.
      ['pointerdown', 'touchstart', 'mousedown', 'click'].forEach(type => {
        this.menu.addEventListener(type, event => {
          event.stopPropagation();
          const row = event.target.closest('[data-option-index]');
          if (type === 'mousedown' && row) event.preventDefault();
          if (type === 'click' && row) this.commit(Number(row.dataset.optionIndex));
        });
      });
      this.menu.addEventListener('pointermove', event => {
        if (event.pointerType === 'touch') return;
        const row = event.target.closest('[data-option-index]');
        if (row && row.getAttribute('aria-disabled') !== 'true') this.activate(Number(row.dataset.optionIndex), false);
      });
      this.observer = new MutationObserver(() => { this.dirty = true; this.sync(); });
      this.observer.observe(select, { attributes: true, childList: true, subtree: true, characterData: true });
      this.dirty = true;
      this.sync();
    }
    sync() {
      const select = this.select;
      if (!select.isConnected || !this.trigger.isConnected || this.trigger.parentElement !== select.parentElement) return;
      const visible = available(select);
      this.trigger.hidden = !visible;
      this.trigger.disabled = select.matches(':disabled');
      if (!visible || this.trigger.disabled) this.close();
      setAttr(select, 'data-hux-dropdown-native', '');
      setAttr(select, 'tabindex', '-1');
      setAttr(select, 'aria-hidden', 'true');
      const option = select.options[select.selectedIndex];
      setText(this.value, option ? option.label : 'Choose an option');
      const label = labelFor(select, this.kind);
      [this.trigger, this.menu].forEach(node => {
        setAttr(node, 'aria-labelledby', label.ids);
        setAttr(node, 'aria-label', label.ids ? null : label.text);
      });
      setAttr(this.trigger, 'aria-required', select.required ? 'true' : null);
      setAttr(this.trigger, 'aria-invalid', this.invalid ? 'true' : select.getAttribute('aria-invalid'));
      setText(this.error, this.invalid ? select.validationMessage : '');
      const description = [select.getAttribute('aria-describedby'), this.invalid ? this.error.id : null].filter(Boolean).join(' ');
      setAttr(this.trigger, 'aria-describedby', description || null);
      if (opened === this) {
        if (this.dirty) {
          // Cancel a pending highlight when app data is replaced under an open menu.
          this.render();
          this.activate(select.selectedIndex);
        } else if (this.lastIndex !== select.selectedIndex) {
          this.render();
          this.activate(select.selectedIndex);
        }
      }
      this.lastIndex = select.selectedIndex;
    }
    render() {
      this.menu.replaceChildren();
      this.rows = [];
      this.active = -1;
      this.trigger.removeAttribute('aria-activedescendant');
      let group = null;
      let host = this.menu;
      Array.from(this.select.options).forEach((option, index) => {
        const parent = option.parentElement;
        if (option.hidden || (parent.tagName === 'OPTGROUP' && parent.hidden)) return;
        if (parent.tagName === 'OPTGROUP') {
          if (group !== parent) {
            group = parent;
            const item = document.createElement('li');
            item.setAttribute('role', 'presentation');
            const heading = document.createElement('span');
            heading.className = 'hux-dropdown__group-label';
            heading.textContent = parent.label;
            host = document.createElement('ul');
            host.setAttribute('role', 'group');
            host.setAttribute('aria-label', parent.label);
            item.append(heading, host);
            this.menu.appendChild(item);
          }
        } else { group = null; host = this.menu; }
        const row = document.createElement('li');
        row.id = this.id + '-option-' + index;
        row.dataset.optionIndex = String(index);
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', String(index === this.select.selectedIndex));
        row.setAttribute('aria-disabled', String(option.disabled || (parent.tagName === 'OPTGROUP' && parent.disabled)));
        row.textContent = option.label || '\u00a0';
        host.appendChild(row);
        this.rows[index] = { row, option };
      });
      this.dirty = false;
    }
    choices() { return this.rows.map((entry, index) => entry && entry.row.getAttribute('aria-disabled') !== 'true' ? index : -1).filter(index => index >= 0); }
    open() {
      this.sync();
      if (this.trigger.disabled || this.trigger.hidden || !available(this.trigger)) return;
      if (opened && opened !== this) opened.close();
      opened = this;
      this.render();
      this.menu.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.trigger.setAttribute('aria-owns', this.menu.id);
      this.trigger.focus({ preventScroll: true });
      this.place();
      this.activate(this.choices().includes(this.select.selectedIndex) ? this.select.selectedIndex : this.choices()[0]);
    }
    close() {
      if (opened === this) opened = null;
      this.menu.hidden = true;
      setAttr(this.trigger, 'aria-expanded', 'false');
      this.trigger.removeAttribute('aria-activedescendant');
      this.trigger.removeAttribute('aria-owns');
      this.buffer = '';
    }
    activate(index, scroll = true) {
      if (!this.rows[index] || this.rows[index].row.getAttribute('aria-disabled') === 'true') return;
      this.active = index;
      this.rows.forEach((entry, rowIndex) => { if (entry) entry.row.classList.toggle('is-active', rowIndex === index); });
      this.trigger.setAttribute('aria-activedescendant', this.rows[index].row.id);
      if (scroll) {
        const row = this.rows[index].row.getBoundingClientRect();
        const menu = this.menu.getBoundingClientRect();
        if (row.top < menu.top) this.menu.scrollTop -= menu.top - row.top;
        else if (row.bottom > menu.bottom) this.menu.scrollTop += row.bottom - menu.bottom;
      }
    }
    commit(index) {
      const entry = this.rows[index];
      const select = this.select;
      if (!available(select) || !entry || select.options[index] !== entry.option || select.matches(':disabled') || entry.option.disabled || entry.option.hidden || entry.option.parentElement.hidden || entry.option.parentElement.disabled) {
        this.close();
        return;
      }
      const changed = select.selectedIndex !== index;
      this.close();
      select.selectedIndex = index;
      this.invalid = false;
      this.sync();
      if (changed) select.dispatchEvent(new Event('change', { bubbles: true }));
      // Quantity 10+ and app handlers own all focus after change dispatch.
    }
    key(event) {
      if (event.altKey || event.ctrlKey || event.metaKey) return;
      const key = event.key;
      if (key === 'Tab') { this.close(); return; }
      if (key === 'Escape') { if (opened === this) { event.preventDefault(); event.stopPropagation(); this.close(); } return; }
      if (['ArrowDown', 'ArrowUp', 'Home', 'End', 'PageDown', 'PageUp'].includes(key)) {
        event.preventDefault(); event.stopPropagation();
        const wasOpen = opened === this;
        if (!wasOpen) this.open();
        const choices = this.choices();
        let position = Math.max(0, choices.indexOf(this.active));
        if (key === 'Home') position = 0;
        else if (key === 'End') position = choices.length - 1;
        else if (wasOpen) position += (key === 'ArrowUp' ? -1 : key === 'PageUp' ? -10 : key === 'PageDown' ? 10 : 1);
        this.activate(choices[Math.max(0, Math.min(choices.length - 1, position))]);
      } else if (key === 'Enter' || (key === ' ' && (!this.buffer || Date.now() - this.typedAt > 500))) {
        event.preventDefault(); event.stopPropagation();
        if (opened === this) this.commit(this.active); else this.open();
      } else if (key.length === 1) {
        event.preventDefault(); event.stopPropagation();
        if (opened !== this) this.open();
        const now = Date.now();
        this.buffer = (now - this.typedAt > 500 ? '' : this.buffer) + key.toLocaleLowerCase();
        this.typedAt = now;
        const choices = this.choices();
        const repeated = this.buffer.split('').every(letter => letter === this.buffer[0]);
        const query = repeated ? this.buffer[0] : this.buffer;
        const start = Math.max(0, choices.indexOf(this.active) + (query.length === 1 ? 1 : 0));
        const ordered = choices.slice(start).concat(choices.slice(0, start));
        const match = ordered.find(index => this.rows[index].option.label.trim().toLocaleLowerCase().startsWith(query));
        if (match != null) this.activate(match);
      }
    }
    place() {
      if (opened !== this) return;
      const rect = this.trigger.getBoundingClientRect();
      const viewport = window.visualViewport;
      const leftEdge = viewport?.offsetLeft || 0;
      const topEdge = viewport?.offsetTop || 0;
      const width = viewport?.width || window.innerWidth;
      const height = viewport?.height || window.innerHeight;
      const bottom = topEdge + height - 8;
      if (!available(this.trigger) || rect.bottom < topEdge || rect.top > bottom) { this.close(); return; }
      let top = topEdge + 8;
      document.querySelectorAll('.site-header, [data-site-header], .site-header-sticky').forEach(header => {
        const bounds = header.getBoundingClientRect();
        const position = getComputedStyle(header).position;
        if ((position === 'fixed' || position === 'sticky') && bounds.top <= top && bounds.bottom > top && bounds.bottom < bottom) top = bounds.bottom + 8;
      });
      const below = Math.max(0, bottom - rect.bottom);
      const above = Math.max(0, rect.top - top);
      const up = below < Math.min(300, height * 0.46) && above > below;
      const maximum = Math.max(0, Math.min(360, height * 0.46, up ? above : below));
      if (maximum < 40) { this.close(); return; }
      const menuWidth = Math.min(Math.max(rect.width, this.kind === 'quantity' ? 100 : 220), width - 16);
      const x = Math.max(leftEdge + 8, Math.min(rect.left, leftEdge + width - menuWidth - 8));
      this.menu.style.width = menuWidth + 'px';
      this.menu.style.maxHeight = maximum + 'px';
      this.menu.style.left = x + 'px';
      this.menu.style.top = '0px';
      const menuHeight = this.menu.getBoundingClientRect().height;
      const y = up ? rect.top - menuHeight + 1 : rect.bottom - 1;
      this.menu.style.top = y + 'px';
      // A modal/body transform changes the containing block of fixed descendants.
      const actual = this.menu.getBoundingClientRect();
      this.menu.style.left = (x + x - actual.left) + 'px';
      this.menu.style.top = (y + y - actual.top) + 'px';
    }
    destroy() {
      this.close();
      this.observer.disconnect();
      this.select.removeEventListener('change', this.onNative);
      this.select.removeEventListener('input', this.onNative);
      this.select.removeEventListener('focus', this.onNativeFocus);
      this.trigger.remove();
      this.error.remove();
      this.menu.remove();
      this.select.removeAttribute('data-hux-dropdown-native');
      setAttr(this.select, 'tabindex', this.oldTab);
      setAttr(this.select, 'aria-hidden', this.oldHidden);
      instances.delete(this.select);
    }
  }

  function scan() {
    pending = false;
    instances.forEach(instance => {
      if (!instance.select.isConnected || !instance.trigger.isConnected || !instance.menu.isConnected || instance.trigger.parentElement !== instance.select.parentElement || !eligible(instance.select)) instance.destroy();
      else instance.sync();
    });
    document.querySelectorAll(candidates).forEach(select => {
      if (eligible(select) && !instances.has(select)) instances.set(select, new Dropdown(select));
    });
  }
  function schedule() {
    if (!pending) { pending = true; queueMicrotask(scan); }
  }
  function start() {
    // If the stylesheet fails, keep the ordinary native control fully usable.
    if (getComputedStyle(document.documentElement).getPropertyValue('--hux-dropdown-ready').trim() !== '1') return;
    scan();
    new MutationObserver(mutations => {
      const relevant = mutations.some(mutation => {
        const target = mutation.target;
        if (target.closest?.('[data-hux-dropdown-trigger], [data-hux-dropdown-menu], [data-hux-dropdown-error]')) return false;
        if (mutation.type === 'attributes') {
          return target.matches?.(candidates) || Array.from(instances.keys()).some(select => target.contains(select));
        }
        return [...mutation.addedNodes, ...mutation.removedNodes].some(node => node.nodeType === 1 && (
          node.matches?.('select, [data-hux-dropdown-trigger], [data-hux-dropdown-menu]') ||
          node.querySelector?.('select, [data-hux-dropdown-trigger], [data-hux-dropdown-menu]')
        ));
      });
      if (relevant) schedule();
    }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden', 'inert', 'disabled', 'aria-hidden'] });
    document.addEventListener('pointerdown', event => { if (opened && !opened.trigger.contains(event.target) && !opened.menu.contains(event.target)) opened.close(); });
    document.addEventListener('focusin', event => { if (opened && !opened.trigger.contains(event.target) && !opened.menu.contains(event.target)) opened.close(); });
    document.addEventListener('click', event => {
      const label = event.target.closest('label');
      const instance = label && instances.get(label.control);
      if (instance && !instance.trigger.contains(event.target)) {
        event.preventDefault();
        if (!instance.trigger.disabled && available(instance.trigger)) instance.trigger.focus({ preventScroll: true });
      }
    }, true);
    // Run before the theme's window-level Escape listeners, which close the owning panel.
    window.addEventListener('keydown', event => {
      if (event.key === 'Escape' && opened) { event.preventDefault(); event.stopImmediatePropagation(); opened.close(); }
    }, true);
    document.addEventListener('invalid', event => {
      const instance = instances.get(event.target);
      if (!instance || !available(instance.trigger)) return;
      event.preventDefault();
      instance.invalid = true;
      instance.sync();
      if (!validationFocus) {
        validationFocus = true;
        announce(instance.select.validationMessage);
        instance.trigger.focus({ preventScroll: false });
        setTimeout(() => { validationFocus = false; }, 0);
      }
    }, true);
    ['change', 'input', 'click', 'hux:dropdown-sync'].forEach(type => document.addEventListener(type, schedule));
    document.addEventListener('reset', event => setTimeout(() => {
      instances.forEach(instance => { if (instance.select.form === event.target) instance.invalid = false; });
      scan();
    }, 0), true);
    window.addEventListener('pageshow', scan);
    document.addEventListener('visibilitychange', () => { if (!document.hidden) scan(); });
    window.addEventListener('resize', () => { scan(); opened?.place(); });
    document.addEventListener('scroll', () => opened?.place(), { passive: true, capture: true });
    window.visualViewport?.addEventListener('resize', () => opened?.place());
    window.visualViewport?.addEventListener('scroll', () => opened?.place());
    // selectedIndex/.value writes do not emit events or attribute mutations.
    setInterval(() => {
      if (!document.hidden) instances.forEach(instance => {
        if (instance.select.selectedIndex !== instance.lastIndex) instance.sync();
      });
    }, 250);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
